// components/loyalty/LoyaltyTierBadge.jsx
// Displays user's loyalty tier with badge and progress indicator
// Created: December 30, 2025 - Task 3.1
// ============================================

import React, { useState } from 'react';
import { useQuery, gql } from '@apollo/client';
import { 
  Crown, 
  Star, 
  ChevronRight, 
  ChevronDown,
  TrendingUp,
  ShoppingBag,
  Users,
  Sparkles,
  Gift
} from 'lucide-react';

// GraphQL query for tier progress
const TIER_PROGRESS = gql`
  query TierProgress {
    tierProgress {
      currentTier
      currentLabel
      nextTier
      nextLabel
      purchasesNeeded
      referralsNeeded
      purchaseProgress
      referralProgress
      overallProgress
      isMaxTier
      message
    }
  }
`;

// Tier configuration with colors and icons
const TIER_CONFIG = {
  NEW_USER: {
    label: 'New Member',
    color: 'blue',
    bgGradient: 'from-blue-50 to-blue-100',
    textColor: 'text-blue-700',
    badgeBg: 'bg-blue-50',
    badgeBorder: 'border-blue-300',
    iconBg: 'bg-blue-100',
    progressBg: 'bg-blue-100',
    progressFill: 'bg-blue-500',
    icon: Star,
    maxRedemption: '$15'
  },
  BRONZE: {
    label: 'Bronze',
    color: 'amber',
    bgGradient: 'from-amber-100 to-amber-200',
    textColor: 'text-amber-800',
    badgeBg: 'bg-amber-50',
    badgeBorder: 'border-amber-300',
    iconBg: 'bg-amber-200',
    progressBg: 'bg-amber-100',
    progressFill: 'bg-amber-500',
    icon: Star,
    maxRedemption: '$25'
  },
  SILVER: {
    label: 'Silver',
    color: 'slate',
    bgGradient: 'from-slate-200 to-slate-300',
    textColor: 'text-slate-700',
    badgeBg: 'bg-slate-100',
    badgeBorder: 'border-slate-400',
    iconBg: 'bg-slate-300',
    progressBg: 'bg-slate-200',
    progressFill: 'bg-slate-500',
    icon: Star,
    maxRedemption: '$40'
  },
  GOLD: {
    label: 'Gold',
    color: 'yellow',
    bgGradient: 'from-yellow-200 to-yellow-400',
    textColor: 'text-yellow-800',
    badgeBg: 'bg-yellow-50',
    badgeBorder: 'border-yellow-400',
    iconBg: 'bg-yellow-300',
    progressBg: 'bg-yellow-200',
    progressFill: 'bg-yellow-500',
    icon: Crown,
    maxRedemption: '$75'
  },
  PLATINUM: {
    label: 'Platinum',
    color: 'violet',
    bgGradient: 'from-violet-300 to-purple-400',
    textColor: 'text-violet-900',
    badgeBg: 'bg-gradient-to-r from-violet-100 to-purple-100',
    badgeBorder: 'border-violet-400',
    iconBg: 'bg-violet-300',
    progressBg: 'bg-violet-200',
    progressFill: 'bg-violet-600',
    icon: Crown,
    maxRedemption: 'Unlimited'
  }
};

const LoyaltyTierBadge = ({ 
  size = 'medium',  // 'small', 'medium', 'large'
  showProgress = true,
  showDetails = false,
  expandable = true,
  onViewDetails = null,
  className = ''
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Fetch tier progress from backend
  const { data, loading, error } = useQuery(TIER_PROGRESS, {
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'ignore'
  });
  
  const progress = data?.tierProgress;
  const tier = progress?.currentTier || 'NEW_USER';
  const config = TIER_CONFIG[tier] || TIER_CONFIG.NEW_USER;
  const TierIcon = config.icon;
  
  // Size configurations
  const sizes = {
    small: {
      badge: 'px-2 py-1 text-xs',
      icon: 'w-3 h-3',
      container: 'text-sm'
    },
    medium: {
      badge: 'px-3 py-1.5 text-sm',
      icon: 'w-4 h-4',
      container: 'text-base'
    },
    large: {
      badge: 'px-4 py-2 text-base',
      icon: 'w-5 h-5',
      container: 'text-lg'
    }
  };
  
  const sizeConfig = sizes[size];
  
  if (loading && !progress) {
    return (
      <div className={`animate-pulse ${className}`}>
        <div className="h-8 bg-gray-200 rounded-full w-32"></div>
      </div>
    );
  }
  
  if (error && !progress) {
    return null; // Fail silently
  }
  
  const handleToggleExpand = () => {
    if (expandable) {
      setIsExpanded(!isExpanded);
    }
  };
  
  const handleViewDetails = () => {
    if (onViewDetails) {
      onViewDetails();
    }
  };

  return (
    <div className={`${sizeConfig.container} ${className}`}>
      {/* Main Badge */}
      <div 
        className={`inline-flex items-center gap-2 ${config.badgeBg} ${config.badgeBorder} border rounded-full ${sizeConfig.badge} cursor-pointer transition-all hover:shadow-md`}
        onClick={handleToggleExpand}
      >
        <div className={`${config.iconBg} rounded-full p-1`}>
          <TierIcon className={`${sizeConfig.icon} ${config.textColor}`} />
        </div>
        <span className={`font-semibold ${config.textColor}`}>
          {progress?.currentLabel || config.label}
        </span>
        {expandable && showProgress && !progress?.isMaxTier && (
          <ChevronDown 
            className={`w-4 h-4 ${config.textColor} transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
          />
        )}
        {progress?.isMaxTier && (
          <Sparkles className={`w-4 h-4 text-violet-500`} />
        )}
      </div>
      
      {/* Progress Section (when expanded) */}
      {isExpanded && showProgress && !progress?.isMaxTier && progress && (
        <div className="mt-3 bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
          {/* Progress to Next Tier */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">
                Progress to {progress.nextLabel}
              </span>
              <span className="text-sm text-gray-500">
                {Math.round(progress.overallProgress)}%
              </span>
            </div>
            <div className={`h-2 ${config.progressBg} rounded-full overflow-hidden`}>
              <div 
                className={`h-full ${config.progressFill} rounded-full transition-all duration-500`}
                style={{ width: `${Math.min(100, progress.overallProgress)}%` }}
              />
            </div>
          </div>
          
          {/* Requirements */}
          <div className="space-y-3">
            {progress.purchasesNeeded > 0 && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShoppingBag className="w-4 h-4 text-blue-500" />
                  <span className="text-sm text-gray-600">Purchases</span>
                </div>
                <span className="text-sm font-medium text-gray-800">
                  {progress.purchasesNeeded} more needed
                </span>
              </div>
            )}
            
            {progress.referralsNeeded > 0 && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-gray-600">Referrals</span>
                </div>
                <span className="text-sm font-medium text-gray-800">
                  {progress.referralsNeeded} more needed
                </span>
              </div>
            )}
          </div>
          
          {/* Motivation Message */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <div className="flex items-start gap-2">
              <TrendingUp className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-blue-800">{progress.message}</p>
            </div>
          </div>
          
          {/* View Details Link */}
          {onViewDetails && (
            <button 
              onClick={handleViewDetails}
              className="mt-3 w-full flex items-center justify-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              View All Tiers
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>
      )}
      
      {/* Platinum Max Tier Message */}
      {isExpanded && progress?.isMaxTier && (
        <div className="mt-3 bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-violet-200 rounded-full flex items-center justify-center flex-shrink-0">
              <Crown className="w-5 h-5 text-violet-700" />
            </div>
            <div>
              <p className="font-semibold text-violet-900">You've reached the top! 🎉</p>
              <p className="text-sm text-violet-700 mt-1">
                As a Platinum member, you enjoy unlimited token redemption and your tokens never expire.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Compact Details (when showDetails is true but not expandable) */}
      {showDetails && !expandable && progress && !progress.isMaxTier && (
        <div className="mt-2 flex items-center gap-4 text-xs text-gray-500">
          <span className="flex items-center gap-1">
            <Gift className="w-3 h-3" />
            Max: {config.maxRedemption}
          </span>
          <span className="flex items-center gap-1">
            <TrendingUp className="w-3 h-3" />
            {Math.round(progress.overallProgress)}% to {progress.nextLabel}
          </span>
        </div>
      )}
    </div>
  );
};

// Compact inline version for headers
export const LoyaltyTierBadgeInline = ({ className = '' }) => {
  const { data } = useQuery(TIER_PROGRESS, {
    fetchPolicy: 'cache-first',
    errorPolicy: 'ignore'
  });
  
  const tier = data?.tierProgress?.currentTier || 'NEW_USER';
  const config = TIER_CONFIG[tier];
  const TierIcon = config.icon;
  
  return (
    <div className={`inline-flex items-center gap-1 ${config.badgeBg} ${config.badgeBorder} border rounded-full px-2 py-0.5 ${className}`}>
      <TierIcon className={`w-3 h-3 ${config.textColor}`} />
      <span className={`text-xs font-medium ${config.textColor}`}>
        {config.label}
      </span>
    </div>
  );
};

export default LoyaltyTierBadge;